import React, { useState, useEffect, useMemo } from "react";
import { hasPermission } from "../../../utils/helper";
import { AppMainModules, FinanceSubModules } from "../../../utils/app-constant";
import { FiPercent, FiCheckSquare, FiColumns } from "react-icons/fi";
import Breadcrumbs from "../../../components/Breadcrumbs";
import CustomTabs from "../../../components/CustomTabs";
import { apiService } from "../../../services/apiService";
import { successNotify, errorNotify } from "../../../utils/toast";
import { plusIcon } from "../../../utils/svgs";

// Sub-components
import TaxRulesTab from "./components/TaxRulesTab";
import ApprovalWorkflowTab from "./components/ApprovalWorkflowTab";
import InvoiceColumnsTab from "./components/InvoiceColumnsTab";

import "../../../styles/FinanceSettings.css";
import "../../../styles/Access.css";

const defaultSettings = {
  taxRules: [],
  approvalWorkflows: [],
  invoiceColumnProfiles: []
};

const FinanceSettings = () => {
  const canViewTaxRule = hasPermission({
    permissionSlug: "view-taxrule",
    menuSlug: FinanceSubModules.Settings,
    moduleSlug: AppMainModules.finance,
  });

  const canViewApprovalWorkflow = hasPermission({
    permissionSlug: "view-approval-workflow",
    menuSlug: FinanceSubModules.Settings,
    moduleSlug: AppMainModules.finance,
  });

  const canViewInvoiceColumns = hasPermission({
    permissionSlug: "view-invoice-columns",
    menuSlug: FinanceSubModules.Settings,
    moduleSlug: AppMainModules.finance,
  });

  const canCreateTaxRule = hasPermission({
    permissionSlug: "create-taxrule",
    menuSlug: FinanceSubModules.Settings,
    moduleSlug: AppMainModules.finance,
  });

  const canCreateApprovalWorkflow = hasPermission({
    permissionSlug: "create-approval-workflow",
    menuSlug: FinanceSubModules.Settings,
    moduleSlug: AppMainModules.finance,
  });

  const canCreateInvoiceColumns = hasPermission({
    permissionSlug: "create-invoice-columns",
    menuSlug: FinanceSubModules.Settings,
    moduleSlug: AppMainModules.finance,
  });

  const settingsTabs = useMemo(() => [
    canViewTaxRule && {
      key: "taxRules",
      label: "Tax Rules",
      icon: <FiPercent fontSize={"16px"} />,
    },
    canViewApprovalWorkflow && {
      key: "approvalWorkflows",
      label: "Approval Workflow",
      icon: <FiCheckSquare fontSize={"16px"} />,
    },
    canViewInvoiceColumns && {
      key: "invoiceColumnProfiles",
      label: "Invoice Columns",
      icon: <FiColumns fontSize={"16px"} />,
    },
  ].filter(Boolean), [canViewTaxRule, canViewApprovalWorkflow, canViewInvoiceColumns]);

  const [activeTab, setActiveTab] = useState(() => {
    if (canViewTaxRule) return "taxRules";
    if (canViewApprovalWorkflow) return "approvalWorkflows";
    if (canViewInvoiceColumns) return "invoiceColumnProfiles";
    return "taxRules";
  });

  const canCreateCurrent = useMemo(() => {
    if (activeTab === "taxRules") return canCreateTaxRule;
    if (activeTab === "approvalWorkflows") return canCreateApprovalWorkflow;
    if (activeTab === "invoiceColumnProfiles") return canCreateInvoiceColumns;
    return false;
  }, [activeTab, canCreateTaxRule, canCreateApprovalWorkflow, canCreateInvoiceColumns]);

  const [settings, setSettings] = useState(defaultSettings);
  const [customers, setCustomers] = useState([]);
  const [addTrigger, setAddTrigger] = useState(0);

  // Fetch customers list on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await apiService.getCustomerDropdown();
        if (response && response.data) {
          setCustomers(response.data);
        } else if (Array.isArray(response)) {
          setCustomers(response);
        }
      } catch (error) {
        console.error("Failed to fetch customers", error);
      }
    };
    fetchCustomers();
  }, []);

  const saveToLocalStorage = (updatedSettings) => {
    setSettings(updatedSettings);
  };

  // Reset addTrigger on tab change to prevent child modals from auto-opening on mount
  useEffect(() => {
    setAddTrigger(0);
  }, [activeTab]);

  // Trigger add modal in subcomponent
  const handleAddClick = () => {
    setAddTrigger((prev) => prev + 1);
  };

  // --- TAX RULES CRUD ---
  const handleSaveTaxRule = (rule) => {
    let updated = { ...settings };
    if (rule.rule_id) {
      updated.taxRules = updated.taxRules.map((r) =>
        r.rule_id === rule.rule_id ? rule : r
      );
      successNotify("Tax rule updated successfully");
    } else {
      const newRule = {
        ...rule,
        rule_id: `TXR-${Math.floor(1000 + Math.random() * 9000)}`,
      };
      updated.taxRules.push(newRule);
      successNotify("Tax rule added successfully");
    }
    saveToLocalStorage(updated);
  };

  const handleDeleteTaxRule = (id) => {
    let updated = { ...settings };
    updated.taxRules = updated.taxRules.filter((r) => r.rule_id !== id);
    successNotify("Tax rule removed successfully");
    saveToLocalStorage(updated);
  };

  const getAddButtonText = () => {
    if (activeTab === "taxRules") return "Add Tax Rule";
    if (activeTab === "approvalWorkflows") return "Add Stage";
    if (activeTab === "invoiceColumnProfiles") return "Add Configuration";
    return "Add";
  };

  // Tabs are defined dynamically based on permissions

  const handleTabChange = (key) => {
    setActiveTab(key);
    setAddTrigger(0);
  };

  return (
    <div className="trip_cost_estimation_page finance_settings_page">
      <Breadcrumbs values={["Finance", "Finance Settings"]} />

      <div className="access_view">
        <h2>Finance Settings</h2>
        {canCreateCurrent && (
          <div className="services_buttons">
            <button
              type="button"
              onClick={handleAddClick}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              {plusIcon}
              {getAddButtonText()}
            </button>
          </div>
        )}
      </div>

      <div className="px-4 mt-2">
        <CustomTabs
          tabs={settingsTabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
        />
      </div>

      <div>
        {activeTab === "taxRules" && (
          <TaxRulesTab
            taxRules={settings.taxRules}
            customers={customers}
            onSave={handleSaveTaxRule}
            onDelete={handleDeleteTaxRule}
            addTrigger={addTrigger}
          />
        )}

        {activeTab === "approvalWorkflows" && (
          <ApprovalWorkflowTab
            addTrigger={addTrigger}
          />
        )}

        {activeTab === "invoiceColumnProfiles" && (
          <InvoiceColumnsTab
            addTrigger={addTrigger}
          />
        )}
      </div>
    </div>
  );
};

export default FinanceSettings;
